"use client";

import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Link from "next/link";
import Image from "next/image";
import {
  ArrowRight,
  Check,
  ChevronLeft,
  ChevronRight,
  Copy,
  Lock,
  Video,
  X,
} from "lucide-react";
import clsx from "clsx";
import LogoSlider from "@/components/LogoSlider";
import PolaroidStack from "@/components/PolaroidStack";
import LatestNotes from "@/components/LatestNotes";
import {
  useAnalytics,
  useScrollDepthTracking,
  useSectionTracking,
} from "@/hooks/useAnalytics";
import { useDialKit, type DialConfig } from "dialkit";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import {
  AnimatePresence,
  motion,
  type Transition,
  useReducedMotion,
} from "motion/react";
import Hotjar from "@hotjar/browser";
import { RESUME_URL } from "@/lib/site";

gsap.registerPlugin(ScrollTrigger);

const siteId = 6520894;
const hotjarVersion = 6;

interface HomeProject {
  id: string;
  projectName: string;
  category: string;
  status: string;
  title: string;
  description: string;
  image: string;
  video?: string;
  href?: string;
  featured?: boolean;
  isWip: boolean;
}

const homeProjects: HomeProject[] = [
  {
    id: "animalia",
    projectName: "Animalia",
    category: "Design System",
    status: "Shipped",
    title: "Design System that enabled Brazil's Largest Tech M&A",
    description:
      "Led the strategic evolution of our design system to solve critical business challenges arising from rapid M&A growth.",
    image: "/images/animalia/intro-poster.jpg",
    video: "/video/animalia-intro-card.mp4",
    href: "/projects/animalia",
    featured: true,
    isWip: false,
  },
  {
    id: "barista",
    projectName: "Barista",
    category: "Mobile App · iOS",
    status: "Shipped",
    title: "From Idea to App Store",
    description:
      "An iOS app for brewing coffee — built solo, available in the App Store.",
    image: "/images/barista-thumbnail.png",
    href: "/projects/barista",
    isWip: false,
  },
  {
    id: "us",
    projectName: "Us",
    category: "Mobile App · iOS",
    status: "WIP",
    title: "Keep home life in sync",
    description: "A private project currently in progress.",
    image: "/images/us-thumbnail.jpg",
    isWip: true,
  },
  {
    id: "academia-da-boa",
    projectName: "Academia da Boa",
    category: "UX/UI Design",
    status: "Shipped",
    title: "Academia da Boa",
    description:
      "Study anywhere, anytime — a digital learning platform for bar professionals delivering knowledge with accessibility, consistency, and clear differentiation.",
    image: "/images/ambev-thumb.png",
    href: "/projects/academia-da-boa",
    isWip: false,
  },
  {
    id: "crossx",
    projectName: "Cross X",
    category: "Mobile App Design",
    status: "Shipped",
    title: "Creating spaces for everyone",
    description:
      "Cross X helps customers schedule and register for CrossFit classes, with an achievement bar to set goals, track milestones, and monitor fitness progress.",
    image: "/images/cross-thumb.png",
    href: "/projects/crossx",
    isWip: false,
  },
];

const statusStyles: Record<string, string> = {
  Shipped: "border-green-200 text-green-700 bg-green-50",
  "In Progress": "border-blue-200 text-blue-600 bg-blue-50",
  WIP: "border-amber-200 text-amber-600 bg-amber-50",
  "Contact to view": "border-gray-200 text-gray-500 bg-gray-50",
};

const CONTACT_EMAIL = "milkadesign@gmail.com";
const LINKEDIN_URL = "https://www.linkedin.com/in/amilcar-teixeira";
const SCHEDULE_URL =
  "https://calendar.google.com/calendar/u/0/appointments/schedules/AcZssZ05bh5E_WVcpBulovziq8ztY2VfZJLsteWjQDATxx6ct33FrlFDF1eKcVhi9IKMKFbC_8bscHvh";

// Shared glitch-scramble palette (hero "Amilcar ↔ Milka" + footer "Let's …")
const GLITCH_CHARS = "!<>-_\\/[]{}=+*^?#@$%&";
// Every hue here clears 3:1 on the #fafcfd page background at the large
// type sizes the scramble runs on (WCAG 1.4.3).
const GLITCH_COLORS = [
  "#7c3aed",
  "#6366f1",
  "#c2410c",
  "#9333ea",
  "#2563eb",
  "#ea580c",
];
const FOOTER_GLITCH_WORDS = ["work", "chat", "coffee", "run"];

const workFocusConfig = {
  enabled: true,
  heroOffsetX: [-125, -140, -40, 5],
  cardStagger: [0.1, 0, 0.3, 0.01],
  spring: {
    type: "spring",
    visualDuration: 0.6,
    bounce: 0.1,
  },
} satisfies DialConfig;

const HomePage: React.FC = () => {
  const { trackEngagement, trackContact } = useAnalytics();
  const shouldReduceMotion = useReducedMotion();

  const [emailCopied, setEmailCopied] = useState(false);
  const [emailHovered, setEmailHovered] = useState(false);
  const [isContactMenuOpen, setIsContactMenuOpen] = useState(false);
  const [cursorPos, setCursorPos] = useState({ x: 0, y: 0 });
  const [isWorkExpanded, setIsWorkExpanded] = useState(false);
  const [expandedCardWidth, setExpandedCardWidth] = useState<number | null>(
    null,
  );
  const [railState, setRailState] = useState({
    hasOverflow: false,
    canScrollBack: false,
    canScrollForward: false,
  });
  // Which rail chevron is currently held down, so the keyboard path lights
  // the same button a click would.
  const [pressedDirection, setPressedDirection] = useState<-1 | 1 | null>(null);
  const handleEmailMouseMove = (e: React.MouseEvent) => {
    setCursorPos({ x: e.clientX, y: e.clientY });
  };

  const handleCopyEmail = async (e: React.MouseEvent) => {
    e.preventDefault();
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(CONTACT_EMAIL);
      } else {
        // Fallback for older / insecure-context browsers
        const textarea = document.createElement("textarea");
        textarea.value = CONTACT_EMAIL;
        textarea.style.position = "fixed";
        textarea.style.opacity = "0";
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
      }
      setEmailCopied(true);
      trackContact("email_click", "Email Copy", {
        linkType: "email",
      });
      if (emailCopiedTimerRef.current !== null) {
        window.clearTimeout(emailCopiedTimerRef.current);
      }
      emailCopiedTimerRef.current = window.setTimeout(() => {
        setEmailCopied(false);
        emailCopiedTimerRef.current = null;
      }, 2400);
    } catch (error) {
      console.error("Failed to copy email", error);
    }
  };

  const heroRef = useRef<HTMLElement>(null);
  const selectedWorkRef = useRef<HTMLElement>(null);
  const cardsRef = useRef<HTMLDivElement>(null);
  const aboutRef = useRef<HTMLElement>(null);
  const footerRef = useRef<HTMLElement>(null);
  const nameRef = useRef<HTMLSpanElement>(null);
  const footerWordRef = useRef<HTMLSpanElement>(null);
  const contactMenuRef = useRef<HTMLDivElement>(null);
  const emailCopiedTimerRef = useRef<number | null>(null);
  const scrollHoldFrameRef = useRef<number | null>(null);
  const pressedTimerRef = useRef<number | null>(null);

  const workFocus = useDialKit("Work focus mode", workFocusConfig, {
    id: "portfolio-work-focus",
    persist: true,
  });
  const workFocusTransition =
    shouldReduceMotion || !workFocus.enabled
      ? ({ duration: 0 } satisfies Transition)
      : (workFocus.spring as Transition);
  const workFocusDurationMs =
    shouldReduceMotion || !workFocus.enabled
      ? 0
      : "visualDuration" in workFocus.spring
        ? (workFocus.spring.visualDuration ?? 0.6) * 1000
        : 600;

  useScrollDepthTracking();
  useSectionTracking(heroRef, "Hero Section", 0.5);
  useSectionTracking(selectedWorkRef, "Selected Work Section", 0.3);
  useSectionTracking(aboutRef, "About Section", 0.5);
  useSectionTracking(footerRef, "Contact Section", 0.5);

  useEffect(() => {
    try {
      Hotjar.init(siteId, hotjarVersion);
    } catch (error) {
      console.warn("Failed to initialize Hotjar:", error);
    }

    // ScrollTrigger for project cards — responsive behavior
    const cardsCtx = gsap.context(() => {
      ScrollTrigger.matchMedia({
        "(max-width: 1023px)": function () {
          gsap.fromTo(
            ".project-card",
            { opacity: 0, y: 30 },
            {
              opacity: 1,
              y: 0,
              duration: 0.6,
              stagger: 0.1,
              ease: "power2.out",
              scrollTrigger: {
                trigger: cardsRef.current,
                start: "top 85%",
              },
            },
          );
        },
      });
    }, cardsRef);

    // ScrollTrigger for about section
    const aboutCtx = gsap.context(() => {
      gsap.fromTo(
        ".about-reveal",
        { opacity: 0, y: 30 },
        {
          opacity: 1,
          y: 0,
          duration: 0.7,
          stagger: 0.12,
          ease: "power2.out",
          scrollTrigger: {
            trigger: aboutRef.current,
            start: "top 80%",
          },
        },
      );
    }, aboutRef);

    return () => {
      cardsCtx.revert();
      aboutCtx.revert();
    };
  }, []);

  // Glitch scramble: "Amilcar" ↔ "Milka"
  useEffect(() => {
    const el = nameRef.current;
    if (!el) return;

    const words = ["Amilcar", "Milka"];
    let wordIndex = 0;
    let pauseTimeout: ReturnType<typeof setTimeout>;
    let scrambleInterval: ReturnType<typeof setInterval>;

    const randColor = () =>
      GLITCH_COLORS[Math.floor(Math.random() * GLITCH_COLORS.length)];
    const randChar = () =>
      GLITCH_CHARS[Math.floor(Math.random() * GLITCH_CHARS.length)];

    const scramble = (target: string, onDone?: () => void) => {
      const totalFrames = 16;
      let frame = 0;
      clearInterval(scrambleInterval);
      scrambleInterval = setInterval(() => {
        const progress = frame / totalFrames;
        const revealed = Math.floor(progress * target.length);
        let html = target.slice(0, revealed);
        for (let i = revealed; i < target.length; i++) {
          html += `<span style="color:${randColor()}">${randChar()}</span>`;
        }
        el.innerHTML = html;
        frame++;
        if (frame > totalFrames) {
          clearInterval(scrambleInterval);
          el.textContent = target;
          onDone?.();
        }
      }, 35);
    };

    const cycle = () => {
      wordIndex = (wordIndex + 1) % words.length;
      scramble(words[wordIndex], () => {
        // pause before next scramble: longer on "Amilcar", shorter on "Milka"
        const pause = wordIndex === 0 ? 4000 : 2500;
        pauseTimeout = setTimeout(cycle, pause);
      });
    };

    // First trigger: after hero entrance animation (~0.6s) + reading pause
    pauseTimeout = setTimeout(cycle, 2200);

    return () => {
      clearTimeout(pauseTimeout);
      clearInterval(scrambleInterval);
    };
  }, []);

  // Footer glitch scramble: "Let's work / chat / coffee / run — together",
  // same treatment as the hero's "Amilcar ↔ Milka".
  useEffect(() => {
    const el = footerWordRef.current;
    if (!el) return;

    if (shouldReduceMotion) {
      el.textContent = FOOTER_GLITCH_WORDS[0];
      return;
    }

    let wordIndex = 0;
    let visible = false;
    let pauseTimeout: ReturnType<typeof setTimeout> | null = null;
    let scrambleInterval: ReturnType<typeof setInterval> | null = null;

    const randColor = () =>
      GLITCH_COLORS[Math.floor(Math.random() * GLITCH_COLORS.length)];
    const randChar = () =>
      GLITCH_CHARS[Math.floor(Math.random() * GLITCH_CHARS.length)];

    const scramble = (target: string, onDone?: () => void) => {
      const totalFrames = 16;
      let frame = 0;
      if (scrambleInterval) clearInterval(scrambleInterval);
      scrambleInterval = setInterval(() => {
        const progress = frame / totalFrames;
        const revealed = Math.floor(progress * target.length);
        let html = target.slice(0, revealed);
        for (let i = revealed; i < target.length; i++) {
          html += `<span style="color:${randColor()}">${randChar()}</span>`;
        }
        el.innerHTML = html;
        frame++;
        if (frame > totalFrames) {
          if (scrambleInterval) clearInterval(scrambleInterval);
          scrambleInterval = null;
          el.textContent = target;
          onDone?.();
        }
      }, 35);
    };

    const cycle = () => {
      // Offscreen: skip the scramble, just poll again cheaply.
      if (!visible) {
        pauseTimeout = setTimeout(cycle, 1200);
        return;
      }
      wordIndex = (wordIndex + 1) % FOOTER_GLITCH_WORDS.length;
      scramble(FOOTER_GLITCH_WORDS[wordIndex], () => {
        pauseTimeout = setTimeout(cycle, 2600);
      });
    };

    const observer = new IntersectionObserver(
      ([entry]) => {
        visible = entry.isIntersecting;
      },
      { threshold: 0.1 },
    );
    observer.observe(el);

    pauseTimeout = setTimeout(cycle, 1800);

    return () => {
      observer.disconnect();
      if (pauseTimeout) clearTimeout(pauseTimeout);
      if (scrambleInterval) clearInterval(scrambleInterval);
    };
  }, [shouldReduceMotion]);

  const updateRailState = useCallback(() => {
    const rail = cardsRef.current;
    if (!rail) return;

    const maxScrollLeft = Math.max(0, rail.scrollWidth - rail.clientWidth);
    setRailState({
      hasOverflow: maxScrollLeft > 2,
      canScrollBack: rail.scrollLeft > 2,
      canScrollForward: rail.scrollLeft < maxScrollLeft - 2,
    });
  }, []);

  const holdScrollPosition = useCallback(
    (scrollTop: number) => {
      if (scrollHoldFrameRef.current !== null) {
        window.cancelAnimationFrame(scrollHoldFrameRef.current);
      }

      const holdUntil = performance.now() + workFocusDurationMs + 150;
      const holdPosition = () => {
        window.scrollTo({ top: scrollTop, behavior: "auto" });
        if (performance.now() < holdUntil) {
          scrollHoldFrameRef.current =
            window.requestAnimationFrame(holdPosition);
        } else {
          scrollHoldFrameRef.current = null;
        }
      };

      scrollHoldFrameRef.current = window.requestAnimationFrame(holdPosition);
    },
    [workFocusDurationMs],
  );

  const handleViewWork = useCallback(
    (event?: React.MouseEvent<HTMLButtonElement>) => {
      event?.currentTarget.blur();
      trackEngagement("section_view", "Work Focus - Expand", {
        sectionName: "Work Focus Expanded",
      });

      if (!window.matchMedia("(min-width: 1024px)").matches) {
        const selectedWork = document.getElementById("selected-work-section");
        selectedWork?.scrollIntoView({ behavior: "smooth", block: "start" });
        return;
      }

      const firstCard =
        cardsRef.current?.querySelector<HTMLElement>("[data-work-card]:not([data-featured]) .project-card");

      if (firstCard) {
        setExpandedCardWidth(firstCard.getBoundingClientRect().width);
      }

      setIsWorkExpanded(true);
      holdScrollPosition(0);
    },
    [holdScrollPosition, trackEngagement],
  );

  const handleCollapseWork = useCallback(
    (event?: React.MouseEvent<HTMLButtonElement>) => {
      event?.currentTarget.blur();
      const rail = cardsRef.current;

      rail?.scrollTo({ left: 0, behavior: "auto" });
      setIsWorkExpanded(false);
      setExpandedCardWidth(null);
      setRailState({
        hasOverflow: false,
        canScrollBack: false,
        canScrollForward: false,
      });
      holdScrollPosition(0);
      trackEngagement("section_view", "Work Focus - Collapse", {
        sectionName: "Work Focus Collapsed",
      });
    },
    [holdScrollPosition, trackEngagement],
  );

  // Reads live scroll position rather than railState so the keyboard path
  // can't act on a stale closure — same 2px tolerance as updateRailState,
  // which keeps this in lockstep with the buttons' disabled state.
  const scrollProjectRail = useCallback(
    (direction: -1 | 1) => {
      const rail = cardsRef.current;
      const firstCard = rail?.querySelector<HTMLElement>("[data-work-card]");
      if (!rail || !firstCard) return false;

      const maxScrollLeft = Math.max(0, rail.scrollWidth - rail.clientWidth);
      if (direction === -1 && rail.scrollLeft <= 2) return false;
      if (direction === 1 && rail.scrollLeft >= maxScrollLeft - 2) return false;

      const railStyles = window.getComputedStyle(rail);
      const gap =
        Number.parseFloat(railStyles.columnGap || railStyles.gap) || 0;
      const distance = firstCard.getBoundingClientRect().width + gap;

      rail.scrollBy({
        left: direction * distance,
        behavior: shouldReduceMotion ? "auto" : "smooth",
      });
      return true;
    },
    [shouldReduceMotion],
  );

  // Both the click and keyboard paths go through here, so the pressed
  // feedback is identical by construction. The 1.2s timer is only a safety
  // net for a missed keyup; the normal clear path is the keyup handler.
  const nudgeRail = useCallback(
    (direction: -1 | 1) => {
      if (!scrollProjectRail(direction)) return false;
      if (pressedTimerRef.current !== null) {
        window.clearTimeout(pressedTimerRef.current);
      }
      setPressedDirection(direction);
      pressedTimerRef.current = window.setTimeout(() => {
        setPressedDirection(null);
        pressedTimerRef.current = null;
      }, 1200);
      return true;
    },
    [scrollProjectRail],
  );

  useEffect(() => {
    if (!isWorkExpanded) return;

    const rail = cardsRef.current;
    const handleResize = () => updateRailState();
    const frame = window.requestAnimationFrame(updateRailState);

    rail?.addEventListener("scroll", updateRailState, { passive: true });
    window.addEventListener("resize", handleResize);

    return () => {
      window.cancelAnimationFrame(frame);
      rail?.removeEventListener("scroll", updateRailState);
      window.removeEventListener("resize", handleResize);
    };
  }, [isWorkExpanded, expandedCardWidth, updateRailState]);

  useEffect(() => {
    const desktopQuery = window.matchMedia("(min-width: 1024px)");
    const handleBreakpointChange = (event: MediaQueryListEvent) => {
      if (!event.matches) {
        cardsRef.current?.scrollTo({ left: 0, behavior: "auto" });
        setIsWorkExpanded(false);
        setExpandedCardWidth(null);
        setRailState({
          hasOverflow: false,
          canScrollBack: false,
          canScrollForward: false,
        });
      }
    };

    desktopQuery.addEventListener("change", handleBreakpointChange);
    return () =>
      desktopQuery.removeEventListener("change", handleBreakpointChange);
  }, []);

  useEffect(() => {
    const frame = window.requestAnimationFrame(() => ScrollTrigger.refresh());
    const timeout = window.setTimeout(
      () => ScrollTrigger.refresh(),
      workFocusDurationMs + 100,
    );

    return () => {
      window.cancelAnimationFrame(frame);
      window.clearTimeout(timeout);
    };
  }, [isWorkExpanded, workFocusDurationMs]);

  useEffect(() => {
    if (!isWorkExpanded) return;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        handleCollapseWork();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [handleCollapseWork, isWorkExpanded]);

  // Arrow keys drive the rail from anywhere while focus mode is open — the
  // rail no longer has to hold focus first. Bound to window (not document)
  // so dialkit's own window listener, registered at mount, runs first and
  // the defaultPrevented check can actually catch it.
  useEffect(() => {
    if (!isWorkExpanded) return;

    const clearPressed = () => {
      if (pressedTimerRef.current !== null) {
        window.clearTimeout(pressedTimerRef.current);
        pressedTimerRef.current = null;
      }
      setPressedDirection(null);
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented) return;
      if (event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) {
        return;
      }
      if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;

      const target = event.target as HTMLElement | null;
      if (
        target?.closest?.(
          "input, textarea, select, [contenteditable=''], [contenteditable='true']",
        )
      ) {
        return;
      }

      // Only swallow the key when it actually moved the rail, so arrows keep
      // working normally at either end.
      if (nudgeRail(event.key === "ArrowLeft" ? -1 : 1)) {
        event.preventDefault();
      }
    };

    const handleKeyUp = (event: KeyboardEvent) => {
      if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
      if (pressedTimerRef.current !== null) {
        window.clearTimeout(pressedTimerRef.current);
      }
      pressedTimerRef.current = window.setTimeout(() => {
        setPressedDirection(null);
        pressedTimerRef.current = null;
      }, 160);
    };

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);
    window.addEventListener("blur", clearPressed);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
      window.removeEventListener("blur", clearPressed);
      clearPressed();
    };
  }, [isWorkExpanded, nudgeRail]);

  useEffect(() => {
    if (!isContactMenuOpen) return;

    const handlePointerDown = (event: PointerEvent) => {
      if (
        contactMenuRef.current &&
        !contactMenuRef.current.contains(event.target as Node)
      ) {
        setIsContactMenuOpen(false);
      }
    };
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsContactMenuOpen(false);
      }
    };

    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isContactMenuOpen]);

  useEffect(
    () => () => {
      if (scrollHoldFrameRef.current !== null) {
        window.cancelAnimationFrame(scrollHoldFrameRef.current);
      }
      if (emailCopiedTimerRef.current !== null) {
        window.clearTimeout(emailCopiedTimerRef.current);
      }
      if (pressedTimerRef.current !== null) {
        window.clearTimeout(pressedTimerRef.current);
      }
    },
    [],
  );

  return (
    <div style={{ background: "#fafcfd" }}>
      {/* The desktop grid is the sticky hero's natural scroll boundary. */}
      <div
        className="lg:grid lg:grid-cols-2 lg:items-start"
        style={{ overflowAnchor: "none" }}
      >
        {/* Hero Section */}
        <motion.section
          ref={heroRef}
          id="hero-section"
          className="min-h-screen flex items-end pb-24 pt-36 px-6 md:px-12 lg:px-20 lg:sticky lg:top-0 lg:col-start-1 lg:row-start-1 lg:h-screen lg:self-start lg:z-10"
          style={{ background: "#fafcfd" }}
          initial={false}
          animate={{
            x: isWorkExpanded ? `${workFocus.heroOffsetX}%` : "0%",
            opacity: isWorkExpanded ? 0 : 1,
          }}
          transition={workFocusTransition}
          aria-hidden={isWorkExpanded}
          inert={isWorkExpanded ? true : undefined}
        >
          <div className="max-w-5xl w-full">
            <p className="hero-eyebrow-name text-[32px] text-[#6f6f6f] mb-4 font-pp-neuebit">
              Hi, I&apos;m <span ref={nameRef}>Amilcar</span>
            </p>
            <h1 className="hero-headline text-[clamp(28px,3.5vw,48px)] font-semibold leading-[1.05] text-[#111111] font-pp-acma mb-8">
              <span className="block">Staff Product Design</span>
              <span className="block">
                Building{" "}
                <a
                  href="#selected-work-section"
                  className="design-systems-shimmer hover:opacity-70 transition-opacity duration-200"
                >
                  design systems
                </a>{" "}
                <br></br> used by millions.
                <span className="block">
                  I design experiences by blending <br></br>design, code and AI.
                </span>{" "}
              </span>
            </h1>
            <p className="hero-subtitle text-[16px] md:text-[19px] text-[#6b6b6b] max-w-md leading-relaxed mb-10">
              I design the parts that hold everything together. The tokens, the
              components, the decisions. The stuff most people never see.
              That&apos;s the part I care about most.
            </p>
            <div className="hero-cta flex flex-col sm:flex-row gap-6">
              <button
                onClick={handleViewWork}
                className="text-[#111111] border-b border-[#111111] pb-0.5 text-[16px] font-medium hover:opacity-60 transition-opacity duration-200 text-left w-fit cursor-pointer"
              >
                View my work →
              </button>
              <div
                ref={contactMenuRef}
                className="relative w-fit"
                onMouseEnter={() => setIsContactMenuOpen(true)}
                onMouseLeave={() => setIsContactMenuOpen(false)}
                onFocus={() => setIsContactMenuOpen(true)}
                onBlur={(event) => {
                  if (
                    !event.currentTarget.contains(event.relatedTarget as Node)
                  ) {
                    setIsContactMenuOpen(false);
                  }
                }}
              >
                <button
                  type="button"
                  onClick={() => {
                    setIsContactMenuOpen(true);
                    trackEngagement("section_view", "Hero CTA - Contact Menu");
                  }}
                  aria-expanded={isContactMenuOpen}
                  aria-controls="hero-contact-menu"
                  className="text-[#6b6b6b] border-b border-[#e8e8e8] pb-0.5 text-[16px] font-medium hover:text-[#111111] transition-colors duration-200 text-left w-fit cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#111111] focus-visible:ring-offset-4"
                >
                  Get in touch
                </button>

                <AnimatePresence>
                  {isContactMenuOpen && (
                    <motion.div
                      id="hero-contact-menu"
                      role="group"
                      aria-label="Contact options"
                      className="absolute bottom-full left-0 z-30 flex gap-3 pb-3 sm:left-1/2 sm:-translate-x-1/2"
                      initial={
                        shouldReduceMotion
                          ? false
                          : { opacity: 0, y: 10, scale: 0.94 }
                      }
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: 6, scale: 0.96 }}
                      transition={{ duration: shouldReduceMotion ? 0 : 0.18 }}
                    >
                      <motion.button
                        type="button"
                        onClick={handleCopyEmail}
                        aria-label={
                          emailCopied
                            ? "Email copied"
                            : `Copy ${CONTACT_EMAIL}`
                        }
                        title={emailCopied ? "Copied!" : "Copy email"}
                        className="group/contact-action relative grid h-14 w-14 shrink-0 place-items-center rounded-full border border-black/10 bg-white text-[#111111] shadow-[0_8px_24px_rgba(0,0,0,0.12)] transition-[color,background-color,box-shadow] hover:bg-[#111111] hover:text-white hover:shadow-[0_12px_28px_rgba(17,17,17,0.2)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#111111] focus-visible:ring-offset-2"
                        whileHover={shouldReduceMotion ? undefined : { y: -4 }}
                        whileTap={shouldReduceMotion ? undefined : { scale: 0.94 }}
                      >
                        <span className="relative z-[1] grid place-items-center">
                          <i
                            className="an an-envelope text-[22px]"
                            aria-hidden="true"
                          />
                          {emailCopied && (
                            <span className="absolute -bottom-1.5 -right-2 grid h-4 w-4 place-items-center rounded-full bg-[#111111] text-white ring-2 ring-white/80">
                              <i
                                className="an an-check text-[9px]"
                                aria-hidden="true"
                              />
                            </span>
                          )}
                        </span>
                        <span className="pointer-events-none absolute -top-9 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-[#111111] px-2.5 py-1 text-[11px] font-medium text-white opacity-0 transition-opacity group-hover/contact-action:opacity-100 group-focus-visible/contact-action:opacity-100">
                          {emailCopied ? "Copied!" : "Copy email"}
                        </span>
                      </motion.button>

                      <motion.a
                        href={LINKEDIN_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="Open Amilcar's LinkedIn in a new tab"
                        title="LinkedIn"
                        className="group/contact-action relative grid h-14 w-14 shrink-0 place-items-center rounded-full border border-black/10 bg-white text-[#111111] shadow-[0_8px_24px_rgba(0,0,0,0.12)] transition-[color,background-color,box-shadow] hover:bg-[#0a66c2] hover:text-white hover:shadow-[0_12px_28px_rgba(10,102,194,0.22)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0a66c2] focus-visible:ring-offset-2"
                        whileHover={shouldReduceMotion ? undefined : { y: -4 }}
                        whileTap={shouldReduceMotion ? undefined : { scale: 0.94 }}
                        onClick={() =>
                          trackContact("social_link_click", "LinkedIn", {
                            linkType: "social",
                            socialPlatform: "linkedin",
                          })
                        }
                      >
                        <i
                          className="an an-linkedin-logo relative z-[1] text-[22px]"
                          aria-hidden="true"
                        />
                        <span className="pointer-events-none absolute -top-9 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-[#111111] px-2.5 py-1 text-[11px] font-medium text-white opacity-0 transition-opacity group-hover/contact-action:opacity-100 group-focus-visible/contact-action:opacity-100">
                          LinkedIn
                        </span>
                      </motion.a>

                      <motion.a
                        href={SCHEDULE_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="Book a video call in a new tab"
                        title="Book a call"
                        className="group/contact-action relative grid h-14 w-14 shrink-0 place-items-center rounded-full border border-black/10 bg-white text-[#111111] shadow-[0_8px_24px_rgba(0,0,0,0.12)] transition-[color,background-color,box-shadow] hover:bg-[#111111] hover:text-white hover:shadow-[0_12px_28px_rgba(17,17,17,0.2)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#111111] focus-visible:ring-offset-2"
                        whileHover={shouldReduceMotion ? undefined : { y: -4 }}
                        whileTap={shouldReduceMotion ? undefined : { scale: 0.94 }}
                        onClick={() =>
                          trackContact("social_link_click", "Book a call", {
                            linkType: "social",
                            socialPlatform: "google_calendar",
                          })
                        }
                      >
                        <i
                          className="an an-video-camera relative z-[1] text-[22px]"
                          aria-hidden="true"
                        />
                        <span className="pointer-events-none absolute -top-9 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-[#111111] px-2.5 py-1 text-[11px] font-medium text-white opacity-0 transition-opacity group-hover/contact-action:opacity-100 group-focus-visible/contact-action:opacity-100">
                          Book a call
                        </span>
                      </motion.a>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </div>
        </motion.section>

        {/* Selected Work Section */}
        <motion.section
          ref={selectedWorkRef}
          id="selected-work-section"
          layout
          className={clsx(
            "min-w-0 px-6 md:px-12 lg:px-20 py-24 border-t border-[#e8e8e8] lg:row-start-1 lg:border-t-0",
            {
              "lg:col-span-2 lg:col-start-1 lg:border-l-0 lg:pt-28 lg:pb-20":
                isWorkExpanded,
              "lg:col-start-2 lg:border-l lg:py-36": !isWorkExpanded,
            },
          )}
          transition={{ layout: workFocusTransition }}
        >
          <div className="flex items-center justify-between gap-6 mb-12">
            <h2 className="text-sm uppercase tracking-[0.15em] text-[#6b6b6b] font-normal">
              Selected Work
            </h2>

            {isWorkExpanded && railState.hasOverflow && (
              <motion.div
                className="hidden lg:flex items-center gap-3"
                initial={shouldReduceMotion ? false : { opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={workFocusTransition}
              >
                <div
                  className="flex items-center gap-1"
                  role="group"
                  aria-label="Project rail navigation"
                >
                  <button
                    type="button"
                    onClick={() => nudgeRail(-1)}
                    disabled={!railState.canScrollBack}
                    data-pressed={pressedDirection === -1 ? "true" : undefined}
                    aria-label="Previous project"
                    className={clsx(
                      "inline-grid h-11 w-11 place-items-center rounded-full border bg-white text-[#111111]",
                      "transition-[transform,background-color,border-color] duration-150",
                      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#111111] focus-visible:ring-offset-2",
                      "disabled:cursor-not-allowed disabled:opacity-30",
                      pressedDirection === -1
                        ? clsx(
                            "border-[#111111] bg-[#ebebeb]",
                            !shouldReduceMotion && "scale-95",
                          )
                        : "border-[#d8d8d8] hover:bg-[#f1f1f1]",
                    )}
                  >
                    <ChevronLeft className="h-4 w-4" aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    onClick={() => nudgeRail(1)}
                    disabled={!railState.canScrollForward}
                    data-pressed={pressedDirection === 1 ? "true" : undefined}
                    aria-label="Next project"
                    className={clsx(
                      "inline-grid h-11 w-11 place-items-center rounded-full border bg-white text-[#111111]",
                      "transition-[transform,background-color,border-color] duration-150",
                      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#111111] focus-visible:ring-offset-2",
                      "disabled:cursor-not-allowed disabled:opacity-30",
                      pressedDirection === 1
                        ? clsx(
                            "border-[#111111] bg-[#ebebeb]",
                            !shouldReduceMotion && "scale-95",
                          )
                        : "border-[#d8d8d8] hover:bg-[#f1f1f1]",
                    )}
                  >
                    <ChevronRight className="h-4 w-4" aria-hidden="true" />
                  </button>
                </div>
              </motion.div>
            )}
          </div>

          <motion.div
            ref={cardsRef}
            layout
            role={isWorkExpanded ? "region" : undefined}
            aria-label={isWorkExpanded ? "Selected projects" : undefined}
            tabIndex={isWorkExpanded ? 0 : undefined}
            className={clsx({
              "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-1 min-[1595px]:grid-cols-2 gap-6 lg:gap-8":
                !isWorkExpanded,
              "work-focus-rail grid grid-cols-1 md:grid-cols-2 gap-6 lg:flex lg:flex-nowrap lg:items-stretch lg:gap-8 lg:overflow-x-auto lg:overscroll-x-contain lg:scroll-smooth lg:snap-x lg:snap-mandatory lg:pb-5":
                isWorkExpanded,
            })}
            transition={{ layout: workFocusTransition }}
          >
            {homeProjects.map((project, index) => (
              <motion.div
                key={project.id}
                layout
                data-work-card
                data-featured={project.featured || undefined}
                className={clsx(
                  "min-w-0",
                  project.featured && "col-span-full",
                  isWorkExpanded && "lg:flex-none lg:snap-start",
                )}
                style={
                  isWorkExpanded && expandedCardWidth
                    ? { width: expandedCardWidth, maxWidth: "100%" }
                    : undefined
                }
                animate={
                  isWorkExpanded
                    ? {
                        opacity: [0.45, 1],
                        y: [28, 0],
                        scale: [0.96, 1],
                      }
                    : { opacity: 1, y: 0, scale: 1 }
                }
                transition={{
                  ...workFocusTransition,
                  delay: isWorkExpanded ? index * workFocus.cardStagger : 0,
                  layout: shouldReduceMotion
                    ? { duration: 0 }
                    : {
                        ...workFocusTransition,
                        delay: index * workFocus.cardStagger,
                      },
                }}
              >
                {(() => {
                  const cardClassName = clsx(
                    "project-card group flex h-full flex-col overflow-hidden rounded-[20px] bg-[#f4f4f5] transition-transform duration-200",
                    project.isWip
                      ? "cursor-not-allowed"
                      : "hover:-translate-y-1",
                  );

                  const cardContent = (
                    <>
                      {/* Project media */}
                      <div
                        className={clsx(
                          "relative w-full shrink-0 overflow-hidden aspect-[4/3]",
                          project.featured && !isWorkExpanded && "md:aspect-[2.78] lg:aspect-video min-[1595px]:aspect-[2.78]",
                        )}
                      >
                        {project.video ? (
                          <video
                            className="absolute inset-0 h-full w-full object-cover"
                            src={project.video}
                            poster={project.image}
                            autoPlay={!shouldReduceMotion}
                            loop
                            muted
                            playsInline
                            preload="metadata"
                            aria-label={`${project.projectName} project preview`}
                          />
                        ) : (
                          <div
                            className="absolute inset-0 bg-cover bg-center bg-no-repeat"
                            style={{ backgroundImage: `url('${project.image}')` }}
                            aria-label={`${project.projectName} project preview`}
                            role="img"
                          />
                        )}
                        {project.isWip && (
                          <div className="absolute inset-0 bg-white/10" aria-hidden="true" />
                        )}
                      </div>

                      {/* Project details */}
                      <div className="flex flex-1 flex-col p-6">
                        <div className="mb-3 flex items-center justify-between gap-3">
                          <span className="text-[13px] text-[#9a9a9a]">
                            {project.category}
                          </span>
                          <span
                            className={`inline-flex shrink-0 items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] ${statusStyles[project.status] ?? statusStyles["Contact to view"]}`}
                          >
                            {project.isWip && <Lock className="h-2.5 w-2.5" />}
                            {project.status}
                          </span>
                        </div>
                        <h3 className="mb-2 font-pp-acma text-[22px] font-semibold leading-[1.2] text-[#111111] md:text-[26px]">
                          {project.title}
                        </h3>
                        <p className="mb-4 text-[14px] leading-relaxed text-[#6b6b6b]">
                          {project.description}
                        </p>
                        <span
                          className={clsx(
                            "mt-auto text-[14px] font-medium transition-opacity duration-200",
                            project.isWip
                              ? "text-[#c9c4c4]"
                              : "text-[#111111] group-hover:opacity-60",
                          )}
                        >
                          {project.isWip ? "Unavailable →" : "View case study →"}
                        </span>
                      </div>
                    </>
                  );

                  return project.href ? (
                    <Link
                      href={project.href}
                      className={cardClassName}
                      onClick={() => {
                        trackEngagement("section_view", project.title);
                      }}
                    >
                      {cardContent}
                    </Link>
                  ) : (
                    <div
                      className={cardClassName}
                      aria-disabled="true"
                      aria-label={`${project.projectName}: ${project.title}. Work in progress.`}
                    >
                      {cardContent}
                    </div>
                  );
                })()}
              </motion.div>
            ))}
          </motion.div>
        </motion.section>
      </div>
      {/* end hero + selected work wrapper */}

      {typeof document !== "undefined" &&
        createPortal(
          <AnimatePresence>
            {isWorkExpanded && (
              <motion.button
                type="button"
                onClick={handleCollapseWork}
                className="fixed bottom-6 left-1/2 z-40 hidden min-h-12 -translate-x-1/2 items-center gap-2 rounded-full border border-black/10 bg-[#111111] px-5 text-sm font-medium text-white shadow-xl lg:flex"
                initial={
                  shouldReduceMotion
                    ? false
                    : { opacity: 0, y: 16, scale: 0.96 }
                }
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={
                  shouldReduceMotion
                    ? { opacity: 0 }
                    : { opacity: 0, y: 12, scale: 0.96 }
                }
                transition={workFocusTransition}
                aria-label="Exit work focus mode"
              >
                <X className="h-4 w-4" aria-hidden="true" />
                Exit focus
                <kbd className="ml-1 rounded-md border border-white/20 bg-white/10 px-1.5 py-0.5 text-[11px] font-normal text-white/70">
                  Esc
                </kbd>
              </motion.button>
            )}
          </AnimatePresence>,
          document.body,
        )}

      {/* About Section */}
      <section
        ref={aboutRef}
        id="amilcar-teixeira-section"
        className="relative z-20 bg-[#242424] py-16 sm:py-24 lg:py-32"
      >
        <div className="px-6 md:px-12 lg:px-20">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Content */}
            <div className="text-white space-y-6 about-reveal">
              <h2 className="text-[28px] sm:text-[36px] lg:text-[50px] font-semibold font-pp-acma">
                Amilcar Teixeira
              </h2>
              <div className="space-y-4 text-[16px] lg:text-[18px] leading-[1.6] text-white/80">
                <p>
                  Over 10 years crafting scalable, accessible, and high-impact
                  product experiences. I blend hands-on UI craft with
                  system-level thinking to build design systems from 0 to 1,
                  lead UX audits, and mentor teams.
                </p>
                <p>
                  My focus is on connecting the dots between design consistency,
                  technical scalability, and user-centered outcomes — partnering
                  closely with engineers, PMs, and designers to create
                  frameworks that scale design maturity.
                </p>
              </div>
              <LogoSlider />
            </div>

            {/* Animated polaroid stack */}
            <div className="flex justify-center lg:justify-end about-reveal order-first md:order-none">
              <PolaroidStack />
            </div>
          </div>
        </div>
      </section>

      {/* Notes teaser — hides itself until a note is published */}
      <LatestNotes />

      {/* Footer / Contact Section */}
      <section
        ref={footerRef}
        id="footer-section"
        className="relative z-20 bg-[#fafcfd]"
      >
        <footer className="relative w-full overflow-hidden px-6 md:px-12 lg:px-20 pt-20 sm:pt-24 lg:pt-28 pb-20 sm:pb-24 lg:pb-28">
          <div className="relative z-10">
            <div className="-mx-6 md:-mx-12 lg:-mx-20 mb-12 sm:mb-14 lg:mb-16 h-px bg-[#e8e8e8]" />

            {/* Message left, Connect + Explore columns right. The labels
                share the same row top as "Get in touch" so they align. */}
            <div className="grid grid-cols-1 gap-y-12 sm:gap-y-14 lg:grid-cols-12 lg:gap-x-8">
              <div className="lg:col-span-7">
                {/* Section eyebrow — same pattern as Selected Work / Notes */}
                <p className="mb-12 text-sm uppercase tracking-[0.15em] text-[#6b6b6b]">
                  Get in touch
                </p>
                <p className="font-pp-acma font-light text-[clamp(24px,2.8vw,36px)] leading-[1.3] text-[#16161e]/55">
                  We would love to hear from you.
                </p>
                <h2 className="font-pp-acma text-[clamp(24px,2.8vw,36px)] font-semibold leading-[1.3] text-[#16161e]">
                  Let&apos;s <span ref={footerWordRef}>work</span> —{" "}
                  <span className="font-light italic">together.</span>
                </h2>
                {/* Kinetic CTA — camera badge hides behind the button and
                    slides out on hover; the label rolls up vertically. */}
                <div className="group cursor-magnetic relative mt-8 w-fit">
                  <Link
                    href={SCHEDULE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="relative z-10 inline-flex w-fit items-center rounded-full bg-[#16161e] px-7 py-4 font-pp-neuebit text-[14px] font-bold uppercase tracking-[0.14em] text-white"
                    onClick={() =>
                      trackContact("social_link_click", "Book a call", {
                        linkType: "social",
                        socialPlatform: "google_calendar",
                      })
                    }
                  >
                    <span className="relative block h-[1em] overflow-hidden leading-none">
                      <span className="block transition-transform duration-300 ease-out group-hover:-translate-y-full">
                        Book a call
                      </span>
                      <span
                        aria-hidden="true"
                        className="absolute left-0 top-full block transition-transform duration-300 ease-out group-hover:-translate-y-full"
                      >
                        Book a call
                      </span>
                    </span>
                  </Link>
                  <span
                    aria-hidden="true"
                    className="absolute right-0 top-1/2 z-0 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-[#7c3aed] text-white transition-transform duration-300 ease-out group-hover:translate-x-[60%] group-hover:rotate-[4deg]"
                  >
                    <Video className="h-4 w-4" />
                  </span>
                </div>
              </div>

              <div className="lg:col-span-4 lg:col-start-9">
                <div className="grid grid-cols-2 gap-x-6">
                  <div className="flex flex-col gap-3">
                    <span className="text-sm uppercase tracking-[0.15em] text-[#6b6b6b]">
                      Connect
                    </span>
                    <a
                      href={`mailto:${CONTACT_EMAIL}`}
                      onClick={handleCopyEmail}
                      onMouseEnter={() => setEmailHovered(true)}
                      onMouseLeave={() => setEmailHovered(false)}
                      onMouseMove={handleEmailMouseMove}
                      aria-label="Mail me — copies my email address"
                      className="w-fit cursor-pointer font-pp-neuebit text-[18px] sm:text-[20px] font-bold text-[#16161e]/80 transition-colors duration-200 hover:text-[#16161e]"
                    >
                      Mail me
                    </a>
                    <Link
                      href={LINKEDIN_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-fit font-pp-neuebit text-[18px] sm:text-[20px] font-bold text-[#16161e]/80 transition-colors duration-200 hover:text-[#16161e]"
                      onClick={() =>
                        trackContact("social_link_click", "LinkedIn", {
                          linkType: "social",
                          socialPlatform: "linkedin",
                        })
                      }
                    >
                      LinkedIn
                    </Link>
                    <Link
                      href={RESUME_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-fit font-pp-neuebit text-[18px] sm:text-[20px] font-bold text-[#16161e]/80 transition-colors duration-200 hover:text-[#16161e]"
                      onClick={() =>
                        trackContact("resume_download", "Resume PDF Footer", {
                          linkType: "resume",
                          downloadType: "pdf",
                        })
                      }
                    >
                      Get CV
                    </Link>
                  </div>
                  <div className="flex flex-col gap-3">
                    <span className="text-sm uppercase tracking-[0.15em] text-[#6b6b6b]">
                      Explore
                    </span>
                    <a
                      href="#selected-work-section"
                      className="w-fit font-pp-neuebit text-[18px] sm:text-[20px] font-bold text-[#16161e]/80 transition-colors duration-200 hover:text-[#16161e]"
                    >
                      Works
                    </a>
                    <a
                      href="#amilcar-teixeira-section"
                      className="w-fit font-pp-neuebit text-[18px] sm:text-[20px] font-bold text-[#16161e]/80 transition-colors duration-200 hover:text-[#16161e]"
                    >
                      About
                    </a>
                    <Link
                      href="/notes"
                      className="w-fit font-pp-neuebit text-[18px] sm:text-[20px] font-bold text-[#16161e]/80 transition-colors duration-200 hover:text-[#16161e]"
                    >
                      Notes
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </footer>
      </section>

      {/* Cursor-following copy-email tooltip */}
      {emailHovered && (
        <div
          className="pointer-events-none fixed z-[100] flex items-center gap-1.5 rounded-full bg-white px-3 py-1.5 text-[13px] font-semibold text-[#111111] shadow-lg font-pp-neuebit"
          style={{
            left: cursorPos.x,
            top: cursorPos.y,
            transform: "translate(16px, -50%)",
          }}
        >
          {emailCopied ? (
            <>
              <Check className="w-3.5 h-3.5" />
              <span>Copied!</span>
            </>
          ) : (
            <>
              <Copy className="w-3.5 h-3.5" />
              <span>Copy email</span>
            </>
          )}
        </div>
      )}

      <AnimatePresence>
        {emailCopied && (
          <motion.div
            role="status"
            aria-live="polite"
            className="fixed bottom-6 left-1/2 z-[110] flex -translate-x-1/2 items-center gap-3 whitespace-nowrap rounded-full border border-black/10 bg-white py-2.5 pl-2.5 pr-4 text-[#111111] shadow-[0_12px_36px_rgba(0,0,0,0.18)]"
            initial={
              shouldReduceMotion
                ? false
                : { opacity: 0, y: 16, scale: 0.96 }
            }
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={
              shouldReduceMotion
                ? { opacity: 0 }
                : { opacity: 0, y: 10, scale: 0.96 }
            }
            transition={{ duration: shouldReduceMotion ? 0 : 0.2 }}
          >
            <span className="relative grid h-8 w-8 place-items-center rounded-full bg-[#111111] text-white">
              <i
                className="an an-envelope text-[15px]"
                aria-hidden="true"
              />
              <span className="absolute -bottom-0.5 -right-0.5 grid h-3.5 w-3.5 place-items-center rounded-full bg-white text-[#111111] ring-1 ring-black/10">
                <i
                  className="an an-check text-[8px]"
                  aria-hidden="true"
                />
              </span>
            </span>
            <span className="font-pp-neuebit text-[15px] font-semibold">
              Email copied
            </span>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default HomePage;
